/**
 * Ability status rules — the vocabulary of the TOML `[[<ability>.status]]`
 * table (plan 29 F1/F1b, lane BR 2026-10-11).
 *
 * Dependency-free on purpose: the content compiler's schema
 * (`scripts/content-schemas.ts`) validates authored statuses against these
 * lists, and the server/client runtime (`abilityStatus.ts`) applies them, so
 * there is one vocabulary for both.
 *
 * Two families:
 *  - **cast statuses** land on the recipient when the cast is accepted —
 *    the caster, or the friendly player a `target = "friendly"` cast keeps:
 *    `absorb` (a damage shield), `immunity` (no damage from the listed
 *    schools), `reduction` (a share of incoming damage removed) and
 *    `stat_buff` (derived stats raised for the duration);
 *  - **hit statuses** land on the target of an admitted hit by the ability:
 *    `slow` (movement reduced by a share) and `root` (no movement).
 */

export const CAST_STATUS_KINDS = ['absorb', 'immunity', 'reduction', 'stat_buff', 'outgoing_damage'] as const;
export const HIT_STATUS_KINDS = ['slow', 'root', 'periodic_damage', 'incoming_damage'] as const;
/** Bounded authored schedules; trusted damage values are positive safe integers. */
export const MAX_PERIODIC_TICKS = 8;
export const ABILITY_STATUS_KINDS = [...CAST_STATUS_KINDS, ...HIT_STATUS_KINDS] as const;

export type CastStatusKind = typeof CAST_STATUS_KINDS[number];
export type HitStatusKind = typeof HIT_STATUS_KINDS[number];
export type AbilityStatusKind = typeof ABILITY_STATUS_KINDS[number];

/** Every damage school a strike can carry (mirrors the TOML `school` enum). */
export const DAMAGE_SCHOOLS = ['physical', 'fire', 'frost', 'arcane', 'shadow', 'holy', 'nature', 'chaos', 'healing'] as const;
/** What the authoring alias `"magic"` expands to: every school but physical. */
export const MAGIC_SCHOOLS = ['fire', 'frost', 'arcane', 'shadow', 'holy', 'nature', 'chaos'] as const;

/**
 * Stats a `stat_buff` may raise — the keys the server folds from active auras
 * into `recalculatePlayerStats` (StatusEffectService `AURA_STAT_KEYS`).
 */
export const STATUS_STAT_KEYS = [
  'hp', 'mp', 'attack', 'magicAttack', 'defense',
  'strength', 'dexterity', 'intelligence', 'luck', 'speed', 'criticalDamage',
] as const;

/** No stack of reductions removes more than this share of a hit. */
export const MAX_DAMAGE_REDUCTION = 0.9;
/** A slow never takes more than this share of movement (a root is the full stop). */
export const MAX_SLOW = 0.9;
/** Longest authored status (Mark of the Wild is a ten-minute buff). */
export const MAX_STATUS_DURATION_MS = 600_000;

export function isCastStatusKind(kind: string): kind is CastStatusKind {
  return (CAST_STATUS_KINDS as readonly string[]).includes(kind);
}

export function isHitStatusKind(kind: string): kind is HitStatusKind {
  return (HIT_STATUS_KINDS as readonly string[]).includes(kind);
}
